import { FC } from 'react';
import { DEFAULT_LOCALE } from '@constants/locales';
import {
  fetchInfiniteNews,
  useFetchInfiniteNews,
} from '@hooks/queries/useFetchInfiniteNews';
import {
  fetchPublicity,
  useFetchPublicity,
} from '@hooks/queries/useFetchPublicity';
import { Box, Flex, Loader, Stack, Title } from '@mantine/core';
import { LastNewsCard } from '@molecules/frontoffice/LastNewsCard';
import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { InView } from 'react-intersection-observer';
import { RandomPublicityComponent } from '@organisms/RandomPublicityComponent';
import { SEO } from '@organisms/SEO';
import { fetchCategories } from '@hooks/queries/useFetchCategories';
import { QUERY_KEY } from '@constants/queries';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { fetchPublicConfiguration } from '@hooks/queries/useFetchPublicConfiguration';
import { fetchSEO, useFetchSEO } from '@hooks/queries/useFetchSEO';
import { LoadingStateComponent } from '@molecules/frontoffice/LoadingStateComponent';

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  const queryClient = new QueryClient();

  const paramsPublicity = {
    fetchAll: true,
  };

  const categoriesPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_CATEGORIES, { fetchAll: true }],
    queryFn: async () => {
      return await fetchCategories({ fetchAll: true });
    },
  });

  const configurationsPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_PUBLIC_CONFIGURATION],
    queryFn: async () => {
      return await fetchPublicConfiguration();
    },
  });

  const publicityPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_PUBLICITY, paramsPublicity],
    queryFn: async () => {
      return await fetchPublicity(paramsPublicity);
    },
  });

  const newsPromise = queryClient.prefetchInfiniteQuery({
    queryKey: [QUERY_KEY.FETCH_NEWS_PUBLIC],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchInfiniteNews({ pageParam }),
  });

  const seoPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_SEO],
    queryFn: async () => {
      return await fetchSEO();
    },
  });

  await Promise.all([
    categoriesPromise,
    publicityPromise,
    newsPromise,
    configurationsPromise,
    seoPromise,
  ]);

  return {
    props: {
      ...(await serverSideTranslations(locale ?? DEFAULT_LOCALE, [
        'common',
        'lastNews',
      ])),
      dehydratedState: dehydrate(queryClient),
    },
  };
};

const LastNewsPage: FC = () => {
  const { t } = useTranslation('lastNews');

  const {
    data: news,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useFetchInfiniteNews();

  const { data: publicity } = useFetchPublicity({ fetchAll: true });

  const { data: dataSEO, isLoading: isLoadingSEO } = useFetchSEO();

  const SEOLastNews = dataSEO?.value.find((page) => page.name === 'last-news');

  const aggregatedNews = news?.pages.flatMap((news) => news.data);

  const hasBannerPub =
    publicity?.data && publicity?.data.some((pub) => pub.type === 'BANNER');

  if (isLoadingSEO) {
    return <LoadingStateComponent />;
  }

  return (
    <>
      <SEO
        title={SEOLastNews?.title}
        keywords={SEOLastNews?.keywords}
        description={SEOLastNews?.description}
      />
      <Stack>
        {hasBannerPub && (
          <Box
            display="flex"
            sx={(theme) => ({
              width: '100vw',
              marginLeft: 'calc(50% - 50vw)',
              [`@media (min-width: ${theme.breakpoints.md})`]: {
                width: '100%',
                marginLeft: 0,
              },
            })}
          >
            <RandomPublicityComponent
              index={0}
              type="BANNER"
              publicities={publicity?.data}
            />
          </Box>
        )}
        <Title variant="h1" order={1} my={20}>
          {t('lastNews.title')}
        </Title>
        {aggregatedNews?.map((news, index) =>
          aggregatedNews.length - 1 === index ? (
            <InView
              as="div"
              style={{ padding: 0, margin: 0 }}
              onChange={(inView) => {
                if (
                  hasNextPage &&
                  inView &&
                  aggregatedNews.length - 1 === index
                ) {
                  fetchNextPage();
                }
              }}
              key={index}
              threshold={0.8}
            >
              <LastNewsCard key={news.id} news={news} />
            </InView>
          ) : (
            <LastNewsCard key={news.id} news={news} />
          )
        )}
        {isFetching && (
          <Flex w="100%" justify="center" my={24}>
            <Loader />
          </Flex>
        )}
      </Stack>
    </>
  );
};

export default LastNewsPage;
